import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Share2, FileImage, Calculator, BookOpen, QrCode, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem } from "../ui/carousel";

const SLIDES = [
  {
    key: "share",
    tag: "Your referral link",
    title: "Spread the word, earn more",
    desc: "Share your personal link on WhatsApp & social media — every lead that converts pays you.",
    gradient: "from-[#1B5E20] to-[#2E7D32]",
    icon: Share2,
    ctaLabel: "Open Toolkit",
    to: "/sc/marketing",
  },
  {
    key: "creatives",
    tag: "Ready-made posts",
    title: "Post today's creative",
    desc: "Branded WhatsApp & Instagram posts with your QR code already baked in — just tap and share.",
    gradient: "from-sky-600 to-indigo-700",
    icon: FileImage,
    ctaLabel: "View Creatives",
    to: "/sc/marketing/creatives",
  },
  {
    key: "calculator",
    tag: "Close deals faster",
    title: "Show live savings on the spot",
    desc: "Open the Savings Calculator in front of your customer — real numbers close more deals.",
    gradient: "from-amber-500 to-orange-600",
    icon: Calculator,
    ctaLabel: "Open Calculator",
    to: "/sc/marketing/calculator",
  },
  {
    key: "explainers",
    tag: "Subsidy talking points",
    title: "Explain the subsidy confidently",
    desc: "Quick, accurate answers on PM Surya Ghar slabs and benefits — never fumble a customer question again.",
    gradient: "from-violet-600 to-purple-800",
    icon: BookOpen,
    ctaLabel: "View Explainers",
    to: "/sc/marketing/explainers",
  },
  {
    key: "flyer",
    tag: "Print & hand out",
    title: "Your personal flyer, ready to print",
    desc: "A flyer with your name, QR code and the Purvodaya logo — perfect for local shops & societies.",
    gradient: "from-rose-600 to-pink-700",
    icon: QrCode,
    ctaLabel: "Open My Flyer",
    to: "/sc/marketing/flyer",
  },
];

export default function ScLeadBanner() {
  const navigate = useNavigate();
  const [api, setApi] = useState(null);
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setActive(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api]);

  useEffect(() => {
    if (!api) return;
    timerRef.current = setInterval(() => api.scrollNext(), 4500);
    return () => clearInterval(timerRef.current);
  }, [api]);

  return (
    <div data-testid="sc-lead-banner">
      <Carousel setApi={setApi} opts={{ loop: true }} className="w-full">
        <CarouselContent>
          {SLIDES.map((s) => {
            const Icon = s.icon;
            return (
              <CarouselItem key={s.key}>
                <div
                  data-testid={`sc-promo-slide-${s.key}`}
                  className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${s.gradient} h-[148px] p-4 flex flex-col justify-between shadow-[0_4px_16px_rgba(15,23,42,0.12)]`}
                >
                  <div>
                    <div className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-white/80 bg-black/15 px-2 py-0.5 rounded-full">
                      <Icon className="w-3 h-3" /> {s.tag}
                    </div>
                    <div className="font-brand text-[19px] font-semibold text-white mt-1.5 leading-snug">{s.title}</div>
                    <p className="text-[11px] text-white/85 mt-1 leading-snug max-w-[85%]">{s.desc}</p>
                  </div>
                  <button
                    onClick={() => navigate(s.to)}
                    data-testid={`sc-promo-slide-cta-${s.key}`}
                    className="self-start inline-flex items-center gap-1 bg-white text-[#0F172A] text-[11px] font-bold px-3 py-1.5 rounded-full hover:scale-105 active:scale-95 transition-transform"
                  >
                    {s.ctaLabel} <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      <div className="flex items-center justify-center gap-1.5 mt-2" data-testid="sc-promo-banner-dots">
        {SLIDES.map((s, i) => (
          <button
            key={s.key}
            onClick={() => api?.scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={"h-1.5 rounded-full transition-all " + (active === i ? "w-5 bg-[#1B5E20]" : "w-1.5 bg-slate-300")}
          />
        ))}
      </div>
    </div>
  );
}
